/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Conditional} from "../../Conditional";
import {
    useFetchServiceModel,
    usePrimeCache
} from "../../../Hooks/ServiceHooks";
import {service as ContactService} from "../../../Services/ContactService";
import {service as StateService} from "../../../Services/StateService";
import {StreamBankLink} from "../../Common/StreamBankLink";
import {usePatchContext} from "../../../Hooks/RouteHooks";


export const ContactDetails = (props) => {

    usePrimeCache([StateService, ContactService]);

    const patchContext = usePatchContext();
    const {
        contact:contactFromProps= null,
        stakeholderId,
        linkTarget,
        linkParams = null,
        linkName = true,
        showMissingFields = true,
    } = props;

    const contact = useFetchServiceModel(ContactService, stakeholderId, contactFromProps);
    const address = contact?.StakeholderAddresses?.Address;
    const {
        address1,
        address2,
        city,
        email,
        phone,
        stateId,
        zipcode
    } = address ?? {};

    const displayName = ContactService.GetTitle(contact);
    const state = StateService.CachedTitleById(stateId);

    if (!contact) {
        return null;
    }

    const missingFields = [];
    if (!ContactService.HasRequiredContactInfo(contact)) {
        if (Number(contact?.stakeholderTypeId ?? 1) === 1) {
            if (!contact?.firstName) missingFields.push('first name');
            if (!contact?.lastName) missingFields.push('last name');
        } else {
            if (!contact?.organizationName) missingFields.push('organization name');
        }
        if (!email) missingFields.push('email');
        if (!phone) missingFields.push('phone');
        if (!address1) missingFields.push('address');
        if (!city) missingFields.push('city');
        if (!stateId) missingFields.push('state');
        if (!zipcode) missingFields.push('zip code');
    }

    return <>
        <h4 className="sb-contact-list-node-title">
            {linkName ? (
                <StreamBankLink
                    Context={patchContext({ conservationAction: null })}
                    Tool={`contacts/${stakeholderId}`}
                    ToolParam={linkParams}
                    target={linkTarget}
                >
                    {displayName}
                </StreamBankLink>
            ) : displayName}
        </h4>
        <Conditional Condition={address}>
            <div className="sb-contact-list-node-address">
                <Conditional Condition={address1}>
                    <p><FontAwesomeIcon icon={['fas', 'map-marker-alt']} fixedWidth/> {address1}</p>
                </Conditional>
                <Conditional Condition={address2}>
                    <p className="sb-contact-list-node-address2 sb-contact-list-node-address-indent">{address2}</p>
                </Conditional>
                <Conditional Condition={city || state || zipcode}>
                    <p className="sb-contact-list-node-address-indent">{[[city, state].filter(Boolean).join(', '), zipcode].filter(Boolean).join(' ')}</p>
                </Conditional>
                <Conditional Condition={email}>
                    <p><FontAwesomeIcon icon={['fas', 'envelope']} fixedWidth/> <a
                        href={"mailto:" + email}>{email}</a></p>
                </Conditional>
                <Conditional Condition={phone}>
                    <p><FontAwesomeIcon icon={['fas', 'phone']} fixedWidth/> <a
                        href={"tel:" + phone}>{String(phone).replace(/[\s_]+$/, '')}</a></p>
                </Conditional>
            </div>
        </Conditional>
        {showMissingFields && missingFields.length > 0 && (
            <p className="sb-contact-missing-info">Missing Required Fields:
                <ul>{missingFields.map((field) => <li key={field}>{field}</li>)}</ul>
            </p>
        )}
    </>;
}
